import type { Translations } from '@/i18n/types';

const fr: Translations = {
  /* ── Common ───────────────────────────────────────────────── */
  'common.add': 'Ajouter',
  'common.save': 'Enregistrer',
  'common.create': 'Créer',
  'common.edit': 'Modifier',
  'common.delete': 'Supprimer',
  'common.cancel': 'Annuler',
  'common.close': 'Fermer',
  'common.back': 'Retour',
  'common.skip': 'Ignorer',
  'common.search': 'Rechercher',
  'common.retry': 'Réessayer',
  'common.reloadPage': 'Recharger la page',
  'offline.title': 'Vous êtes hors ligne',
  'offline.message': "Vous n'êtes actuellement pas connecté à Internet.",
  'common.loading': 'Chargement…',
  'common.optional': '(facultatif)',
  'common.yes': 'Oui',
  'common.no': 'Non',
  'common.none': '— Aucun —',
  'common.notSet': '— Non défini —',
  'common.deleting': 'Suppression…',
  'common.applying': 'Application…',
  'common.closeDialog': 'Fermer la boîte de dialogue',
  'common.prev': 'Préc.',
  'common.next': 'Suiv.',

  /* ── Filter UI ────────────────────────────────────────────── */
  'filter.add': 'Ajouter un filtre',
  'filter.allActive': 'Tous les filtres sont actifs',
  'filter.clearAll': 'Tout effacer',
  'filter.clear': 'Effacer',
  'filter.apply': 'Appliquer',
  'filter.cancel': 'Annuler',
  'filter.condition': 'Condition',
  'filter.presets': 'Préréglages',
  'filter.customRange': 'Plage personnalisée',
  'filter.from': 'De',
  'filter.to': 'À',
  'filter.min': 'Min',
  'filter.max': 'Max',
  'filter.value': 'Valeur',
  'filter.searchOptions': 'Rechercher des options...',
  'filter.noOptions': 'Aucune option trouvée',
  'filter.loadingOptions': 'Chargement des options...',

  /* ── Auth ─────────────────────────────────────────────────── */
  'auth.signIn': 'Se connecter',
  'auth.signInWithGoogle': 'Se connecter avec Google',
  'auth.signInWithApple': 'Se connecter avec Apple',
  'auth.or': 'ou',
  'auth.signOut': 'Se déconnecter',
  'auth.welcome': 'Bienvenue',
  'auth.subtitle': 'Kart Editor',
  'auth.signingIn': 'Connexion en cours...',

  /* ── Settings ───────────────────────────────────────────────── */
  'settings.title': 'Paramètres',
  'settings.account': 'Compte',
  'settings.profile': 'Profil',
  'settings.displayName': "Nom d'affichage",
  'settings.saveName': 'Enregistrer le nom',
  'settings.nameSaved': 'Nom mis à jour avec succès',
  'settings.linkedAccounts': 'Comptes associés',
  'settings.linkGoogle': 'Associer un compte Google',
  'settings.linkApple': 'Associer un compte Apple',
  'settings.unlink': 'Dissocier',
  'settings.linked': 'Associé',
  'settings.notLinked': 'Non associé',
  'settings.cannotUnlinkOnlyProvider': 'Au moins une méthode de connexion doit rester associée.',
  'settings.language': 'Langue',
  'settings.tracks': 'Pistes',
  'settings.categories': 'Catégories',
  'settings.drivers': 'Pilotes',
  'settings.userId': 'ID utilisateur',
  'settings.signOut': 'Se déconnecter',
  'settings.connectedApps': 'Applications connectées',
  'settings.connectedAppsDesc': 'Gérez les assistants IA, IDE et outils autorisés à accéder à vos données de télémétrie via MCP.',
  'settings.noConnectedApps': 'Aucune application connectée pour le moment. Autorisez un client MCP (tel que Claude Desktop ou Cursor) pour le voir apparaître ici.',
  'settings.authorizedOn': 'Autorisé le',
  'settings.lastUsed': 'Dernière utilisation',
  'settings.neverUsed': 'Jamais',
  'settings.revoke': 'Révoquer',
  'settings.revokeAppConfirm': "Voulez-vous vraiment révoquer l'accès de cette application ? Toutes les sessions actives cesseront immédiatement de fonctionner.",
  'settings.appRevokedSuccess': "Accès de l'application révoqué avec succès",
  'settings.copyKey': "Copier l'extrait",
  'settings.keyCopied': 'Copié dans le presse-papiers',
  'settings.mcpConfigSnippet': 'Configuration du client MCP',
  'settings.mcpConfigDesc': "Ajoutez cette configuration dans les paramètres MCP de Claude Desktop (claude_desktop_config.json) ou de Cursor. L'authentification s'effectuera automatiquement via votre navigateur :",
  'settings.deleteAccount': 'Supprimer le compte',
  'settings.deleteAccountDesc': 'Supprimez définitivement votre compte ainsi que toutes les sessions, données de télémétrie, analyses enregistrées, équipements, réglages de kart et partages associés.',
  'settings.deleteAccountConfirmTitle': 'Supprimer définitivement le compte ?',
  'settings.deleteAccountConfirmDesc': 'Cette action est irréversible. Toutes vos sessions, données de télémétrie, analyses enregistrées, équipements, réglages de kart, équipes et partages de sessions seront immédiatement et définitivement supprimés.',
  'settings.deleteAccountConfirmPrompt': 'Pour confirmer, saisissez « {phrase} » dans le champ ci-dessous :',
  'settings.deleteAccountConfirmPhrase': 'supprimer mon compte',
  'settings.deleteAccountConfirmButton': 'Supprimer mon compte',

  /* ── Common fields ────────────────────────────────────────── */
  'common.brand': 'Marque',
  'common.type': 'Type',
  'common.nr': 'N°',
  'common.nrInfo': 'Numéro séquentiel permettant d’identifier cette entrée.',
  'common.year': 'Année',
  'common.stiffness': 'Rigidité',
  'common.width': 'Largeur',
  'common.position': 'Position',

  /* ── Tabs ──────────────────────────────────────────────────── */
  'tab.engines': 'Moteurs',
  'tab.tyres': 'Pneus',
  'tab.chassis': 'Châssis',
  'tab.carburetors': 'Carburateurs',
  'tab.axles': 'Arbres',
  'tab.hubs': 'Moyeux',
  'tab.spark_plugs': 'Bougies',
  'tab.tracks': 'Pistes',
  'tab.categories': 'Catégories',
  'tab.drivers': 'Pilotes',
  'tab.races': 'Courses',
  'tab.users': 'Utilisateurs',
  'tab.settings': 'Paramètres',

  /* ── Subtabs ──────────────────────────────────────────────── */

  /* ── Navigation ────────────────────────────────────────────── */
  'nav.administration': 'Administration',

  /* ── CRUD ──────────────────────────────────────────────────── */
  'crud.requiredField': '{field} est requis',
  'crud.updated': '{entity} mis à jour',
  'crud.created': '{entity} créé',
  'crud.deleted': '{entity} supprimé',
  'crud.confirmDelete': 'Supprimer {entity} « {name} » ?\nCette action est irréversible.',
  'crud.emptyState': 'Aucun {title} pour le moment. Ajoutez votre premier {entity} ci-dessus.',
  'crud.editEntity': 'Modifier {entity}',
  'crud.newEntity': 'Nouveau {entity}',

  /* ── Entities ──────────────────────────────────────────────── */
  'entity.engine': 'Moteur',
  'entity.tyre': 'Pneu',
  'entity.chassis': 'Châssis',
  'entity.carburetor': 'Carburateur',
  'entity.axle': 'Arbre',
  'entity.hub': 'Moyeu',
  'entity.spark_plug': 'Bougie',
  'entity.track': 'Piste',
  'entity.category': 'Catégorie',
  'entity.driver': 'Pilote',

  /* ── Sessions ──────────────────────────────────────────────── */

  /* ── Session Setup Dialog ──────────────────────────────────── */

  /* ── Search Help Dialog ────────────────────────────────────── */

  /* ── Import Dialog ─────────────────────────────────────────── */
  'import.title': 'Importer des PDF de course',
  'import.uploadDesc': 'Téléversez des fichiers PDF de résultats de course RMC. Ils seront analysés et présentés pour vérification avant importation.',
  'import.dragDropPdf': 'Glissez-déposez des fichiers PDF ici',
  'import.orBrowse': 'ou',
  'import.browseFiles': 'parcourir les fichiers',
  'import.onlyPdfAccepted': 'Seuls les fichiers PDF sont acceptés.',
  'import.filesSelected': '{n} fichier(s) sélectionné(s)',
  'import.upload': 'Téléverser et analyser',
  'import.uploadingPct': 'Téléversement… {n}%',
  'import.parseFailed': "Échec de l'analyse de la réponse.",
  'import.uploadFailed': 'Échec du téléversement. Veuillez réessayer.',
  'import.connectionLost': 'Connexion perdue.',
  'import.uploadAborted': 'Téléversement annulé.',
  'import.reviewDesc': 'Vérifiez les sessions analysées. Désélectionnez celles que vous ne souhaitez pas importer.',
  'import.selectAll': 'Tout sélectionner',
  'import.deselectAll': 'Tout désélectionner',
  'import.existsInDb': 'Existe déjà — sera écrasé',
  'import.entriesCount': '{n} inscrits',
  'import.submit': 'Importer',
  'import.submitting': 'Importation des sessions…',
  'import.importedCount': '{n} session(s) importée(s).',
  'import.successMessage': 'Sessions importées avec succès !',
  'import.noNewSessions': "Aucune nouvelle session n'a été importée.",
  'import.imported': 'Importées',
  'import.skipped': 'Ignorées',
  'import.errors': 'Erreurs',
  'import.importButton': 'Importer des PDF',

  /* ── Kart Setup Dialog ─────────────────────────────────────── */

  /* ── Setup Tab ──────────────────────────────────────────────── */

  /* ── Tyre Pressures ────────────────────────────────────────── */

  /* ── Front Geometry ────────────────────────────────────────── */

  /* ── Rear Config ───────────────────────────────────────────── */

  /* ── Engine & Gear ──────────────────────────────────────────── */

  /* ── Carburetor Tuning ──────────────────────────────────────── */

  /* ── Chassis Select ─────────────────────────────────────────── */

  /* ── Notes ──────────────────────────────────────────────────── */

  /* ── Propagation Wizard ─────────────────────────────────────── */

  /* ── Propagation field labels ───────────────────────────────── */

  /* ── Tracks ─────────────────────────────────────────────────── */
  'tracks.name': 'Nom de la piste',
  'tracks.latitude': 'Latitude',
  'tracks.longitude': 'Longitude',
  'tracks.searchAddress': 'Rechercher une adresse…',
  'tracks.coordinates': 'Coordonnées',
  'tracks.clickToAdjust': 'Cliquez sur la carte pour ajuster le repère',
  'tracks.tabLocation': 'Emplacement',
  'tracks.tabAliases': 'Alias',
  'tracks.sessionTracks': 'Pistes de session',
  'tracks.raceEventTracks': 'Pistes de course',
  'tracks.noSessionTracks': 'Aucune piste de session disponible',
  'tracks.noRaceEventTracks': 'Aucune piste de course disponible',
  'tracks.linkedAliases': '{sessions} pistes de session · {races} pistes de course',
  'tracks.satelliteImage': 'Image satellite',
  'tracks.fetchImage': "Récupérer l'image satellite",
  'tracks.refetchImage': "Actualiser l'image satellite",
  'tracks.fetchingImage': "Récupération de l'image…",
  'tracks.imageFetched': 'Image satellite récupérée avec succès',
  'tracks.imageFetchFailed': "Échec de la récupération de l'image satellite",
  'tracks.noImage': 'Aucune image satellite',
  'tracks.imageZoom': 'Niveau de zoom',
  'tracks.autoZoom': 'Zoom automatique',

  /* ── Categories ─────────────────────────────────────────────── */
  'categories.name': 'Nom',
  'categories.aliases': 'Alias de catégorie',
  'categories.noAliases': 'Aucun nom de catégorie disponible',
  'categories.linkedAliases': '{count} alias associés',

  /* ── Races ──────────────────────────────────────────────────── */
  'races.title': 'Sessions de course',
  'races.loading': 'Chargement des sessions de course…',
  'races.empty': 'Aucune session de course pour le moment. Importez des PDF de course pour commencer.',
  'races.loadFailed': '⚠ Échec du chargement des sessions de course : {error}',
  'races.date': 'Date',
  'races.track': 'Piste',
  'races.sessionType': 'Type',
  'races.category': 'Catégorie',
  'races.entries': 'Inscrits',
  'races.laps': 'Tours',
  'races.championship': 'Championnat',
  'races.deleteConfirmTitle': 'Supprimer la session de course',
  'races.deleteConfirmMessage': 'Voulez-vous vraiment supprimer la session <strong>{type}</strong> à <strong>{track}</strong> du <strong>{date}</strong> ? Cela supprimera définitivement tous les inscrits et les données de tours.',
  'races.deleteConfirmButton': 'Supprimer la session',
  'races.deleted': 'Session de course supprimée',
  'races.detailTitle': 'Session de course',
  'races.detailSubtitle': '{type} · {track} · {date}',
  'races.position': 'Pos',
  'races.kart': 'Kart',
  'races.driver': 'Pilote',
  'races.firstName': 'Prénom',
  'races.lastName': 'Nom',
  'races.nationality': 'Nat',
  'races.grid': 'Grille',
  'races.bestLap': 'Meilleur tour',
  'races.totalTime': 'Temps total',
  'races.penalty': 'Pén',
  'races.gap': 'Écart',
  'races.status': 'Statut',
  'races.deleteEntry': "Supprimer l'inscription",
  'races.entryDeleted': 'Inscription supprimée',
  'races.saved': 'Session de course enregistrée',
  'races.searchPlaceholder': 'Rechercher des courses… p. ex. Castelletto ou type:race',
  'races.querySyntaxHelp': 'Aide sur la syntaxe de recherche',
  'races.sessionCount': '{count} sessions',
  'races.sessionCountSingular': '1 session',

  /* ── Race Search Help Dialog ──────────────────────────────── */
  'raceSearchHelp.title': 'Syntaxe de recherche des courses',
  'raceSearchHelp.subtitle': 'Filtrez les sessions de course à l’aide de paires champ:valeur. Plusieurs termes sont combinés avec ET.',
  'raceSearchHelp.bareText': '(texte libre)',
  'raceSearchHelp.bareTextDesc': 'Correspond au nom de la piste ou du championnat contenant ce texte.',
  'raceSearchHelp.track': 'track',
  'raceSearchHelp.trackDesc': 'Correspond au nom de la piste contenant la valeur.',
  'raceSearchHelp.championship': 'championship',
  'raceSearchHelp.championshipDesc': 'Correspond au nom du championnat contenant la valeur.',
  'raceSearchHelp.category': 'category',
  'raceSearchHelp.categoryDesc': 'Correspond au nom de la catégorie contenant la valeur.',
  'raceSearchHelp.type': 'type',
  'raceSearchHelp.typeDesc': 'Correspond au type ou au libellé de la session (p. ex. race, qualifying, heat).',
  'raceSearchHelp.entries': 'entries',
  'raceSearchHelp.entriesDesc': "Filtre par nombre d'inscrits. Prend en charge =, >, >=, <, <= et les plages.",
  'raceSearchHelp.laps': 'laps',
  'raceSearchHelp.lapsDesc': 'Filtre par nombre total de tours. Prend en charge les opérateurs de comparaison et les plages.',
  'raceSearchHelp.season': 'season',
  'raceSearchHelp.seasonDesc': 'Filtre par année de saison du championnat. Prend en charge les opérateurs de comparaison.',
  'raceSearchHelp.from': 'from',
  'raceSearchHelp.fromDesc': 'Courses à partir d’une date donnée. Accepte YYYY-MM-DD ou une valeur relative : -7d, -2w, -3m, -1y.',
  'raceSearchHelp.to': 'to',
  'raceSearchHelp.toDesc': 'Courses jusqu’à une date donnée. Mêmes formats que « from ».',
  'raceSearchHelp.operatorsTitle': 'Opérateurs et logique',
  'raceSearchHelp.numericComparisons': 'Comparaisons numériques :',
  'raceSearchHelp.range': 'Plage :',
  'raceSearchHelp.rangeDesc': '— correspond aux valeurs comprises entre 5 et 20 inclus.',
  'raceSearchHelp.booleanLogic': 'Logique booléenne :',
  'raceSearchHelp.booleanLogicDesc': 'Les termes sont implicitement combinés avec',
  'raceSearchHelp.quotes': 'Guillemets :',
  'raceSearchHelp.quotesDesc': 'Entourez les valeurs contenant des espaces de guillemets doubles, p. ex.',
  'raceSearchHelp.examplesTitle': 'Exemples de requêtes',
  'raceSearchHelp.example1Desc': 'Sessions de course à Castelletto',
  'raceSearchHelp.example2Desc': 'Courses du championnat RMC de la dernière année',
  'raceSearchHelp.example3Desc': 'Courses avec 20+ inscrits et 5+ tours',

  /* ── Pagination ─────────────────────────────────────────────── */
  'pagination.eventsFound': '{n} événement(s) trouvé(s)',

  /* ── Tyre IDs ──────────────────────────────────────────────── */

  /* ── Settings ──────────────────────────────────────────────── */
  'settings.users': 'Utilisateurs',

  /* ── Drivers ──────────────────────────────────────────────── */
  'drivers.canonicalDriver': 'Pilote canonique',
  'drivers.aliases': 'Alias',
  'drivers.aliasesHeader': 'Alias',
  'drivers.raceDrivers': 'Pilotes de course',
  'drivers.sessionDrivers': 'Pilotes de session',
  'drivers.noRaceDrivers': 'Aucun pilote de course disponible',
  'drivers.noSessionDrivers': 'Aucun pilote de session disponible',
  'drivers.selectDriver': 'Sélectionner un pilote…',
  'drivers.searchDrivers': 'Rechercher des pilotes…',
  'drivers.createTitle': 'Nouvel alias de pilote',
  'drivers.editTitle': "Modifier l'alias de pilote",
  'drivers.stepCanonical': 'Sélectionner le pilote',
  'drivers.stepAliases': 'Sélectionner les alias',

  /* ── Sharing ─────────────────────────────────────────────── */
  
  /* ── Users ──────────────────────────────────────────────── */
  'users.title': 'Utilisateurs',
  'users.empty': 'Aucun utilisateur trouvé',
  'users.loading': 'Chargement des utilisateurs...',
  'users.searchPlaceholder': 'Rechercher des utilisateurs...',
  'users.name': 'Nom',
  'users.email': 'E-mail',
  'users.user': 'Utilisateur',
  'users.admin': 'Administrateur',
  'users.owner': 'Propriétaire',
  'users.promote': 'Promouvoir',
  'users.demote': 'Rétrograder',
  'users.promoted': '{name} promu administrateur',
  'users.demoted': '{name} retiré des administrateurs',
  'users.cannotDemoteOwner': 'Le propriétaire ne peut pas être rétrogradé',
  'users.cannotDemoteSelf': 'Vous ne pouvez pas vous rétrograder vous-même',
  'users.confirmPromote': "Promouvoir {name} au rôle d'administrateur ? Il/Elle obtiendra un accès complet à l'éditeur.",
  'users.confirmDemote': "Retirer l'accès administrateur à {name} ? Il/Elle ne pourra plus utiliser l'éditeur.",
  'users.accessDenied': 'Accès refusé',
  'users.accessDeniedMessage': "Vous ne disposez pas des privilèges d'administrateur. Contactez un administrateur pour demander l'accès.",
  'users.joinedAt': 'Inscrit le',
  'users.lastLogin': 'Dernière connexion',
  'users.count': '{count} utilisateurs',
  'users.countSingular': '1 utilisateur',

  /* ── User Search Help Dialog ────────────────────────────── */
  'userSearchHelp.title': "Recherche d'utilisateurs",
  'userSearchHelp.subtitle': 'Filtrez la liste des utilisateurs à l’aide de requêtes libres ou par champ.',
  'userSearchHelp.bareText': 'Texte libre',
  'userSearchHelp.bareTextDesc': 'Correspond à la fois au nom et à l’adresse e-mail.',
  'userSearchHelp.name': 'name:',
  'userSearchHelp.nameDesc': "Filtre par nom d'affichage de l'utilisateur.",
  'userSearchHelp.email': 'email:',
  'userSearchHelp.emailDesc': 'Filtre par adresse e-mail.',
  'userSearchHelp.login': 'login:',
  'userSearchHelp.loginDesc': 'Filtre par date de dernière connexion. Prend en charge des valeurs uniques (-7d) ou des plages (-30d..today). Utilisez des dates relatives (-7d, -2w, -3m), absolues (2026-01-15) ou today.',
  'userSearchHelp.created': 'created:',
  'userSearchHelp.createdDesc': 'Filtre par date de création du compte. Prend en charge des valeurs uniques (-30d) ou des plages (-1y..today). Utilisez des dates relatives (-7d, -2w, -3m), absolues (2026-01-15) ou today.',
  'userSearchHelp.examplesTitle': 'Exemples',
  'userSearchHelp.example1Desc': 'Trouver les utilisateurs nommés Yanick',
  'userSearchHelp.example2Desc': 'Trouver les utilisateurs ayant une adresse Gmail',
  'userSearchHelp.example3Desc': 'Trouver les utilisateurs connectés au cours des 7 derniers jours',
  'userSearchHelp.example4Desc': 'Trouver les utilisateurs créés dans une plage de dates',

  /* ── Bugs tab ───────────────────────────────────────────────── */
  'tab.bugs': 'Bugs',
  'bugs.title': 'Signalements de bugs',
  'bugs.empty': 'Aucun signalement de bug trouvé.',
  'bugs.loading': 'Chargement des signalements de bugs…',
  'bugs.searchPlaceholder': 'Rechercher des signalements de bugs…',
  'bugs.reporter': 'Auteur',
  'bugs.description': 'Description',
  'bugs.pageUrl': 'URL de la page',
  'bugs.status': 'Statut',
  'bugs.statusOpen': 'Ouvert',
  'bugs.statusResolved': 'Résolu',
  'bugs.createdAt': 'Créé le',
  'bugs.resolvedAt': 'Résolu le',
  'bugs.resolve': 'Résoudre',
  'bugs.reopen': 'Rouvrir',
  'bugs.resolved': 'Signalement de bug résolu.',
  'bugs.reopened': 'Signalement de bug rouvert.',
  'bugs.count': '{count} signalements',
  'bugs.countSingular': '1 signalement',
  'bugs.detailTitle': 'Signalement de bug',
  'bugs.userAgent': 'User Agent',
  'bugs.screenSize': "Taille de l'écran",
  'bugs.appLanguage': 'Langue',
  'bugs.copy': 'Copier le texte',
  'bugs.copied': 'Copié dans le presse-papiers',

  /* ── Bug search help ────────────────────────────────────────── */
  'bugSearchHelp.title': 'Syntaxe de recherche',
  'bugSearchHelp.subtitle': 'Utilisez ces préfixes pour affiner votre recherche.',
  'bugSearchHelp.bareText': 'texte libre',
  'bugSearchHelp.bareTextDesc': 'Recherche dans la description et le nom de l’auteur.',
  'bugSearchHelp.status': 'status:open',
  'bugSearchHelp.statusDesc': 'Filtre par statut (open ou resolved).',
  'bugSearchHelp.email': 'email:valeur',
  'bugSearchHelp.emailDesc': 'Filtre par e-mail de l’auteur.',
  'bugSearchHelp.examplesTitle': 'Exemples',
  'bugSearchHelp.example1Desc': 'Trouver tous les signalements de bugs ouverts.',
  'bugSearchHelp.example2Desc': 'Trouver les signalements provenant d’utilisateurs Gmail.',

  /* ── Error auto-reporting & Error Boundary ─────────────────────────── */
  'error.autoReported': 'Une erreur est survenue et a été automatiquement signalée.',
  'errorBoundary.title': 'Une erreur est survenue',
  'errorBoundary.message': 'Une erreur inattendue est survenue et a été signalée au serveur.',
  'errorBoundary.reload': 'Recharger la page',
  'errorBoundary.reset': 'Réessayer',
  'errorBoundary.showDetails': "Afficher les détails de l'erreur",
  'errorBoundary.hideDetails': "Masquer les détails de l'erreur",

  /* ── Profiles ───────────────────────────────────────────────── */
  'tab.profiles': 'Profils',
  'profiles.engineProfiles': 'Profils de moteur',
  'profiles.tyreProfiles': 'Profils de pneus',
  'profiles.addEngineProfile': 'Ajouter un profil de moteur',
  'profiles.editEngineProfile': 'Modifier le profil de moteur',
  'profiles.addTyreProfile': 'Ajouter un profil de pneus',
  'profiles.editTyreProfile': 'Modifier le profil de pneus',
  'profiles.brand': 'Marque',
  'profiles.type': 'Type',
  'profiles.compound': 'Gomme',
  'profiles.compoundDry': 'Sec (Dry)',
  'profiles.compoundWet': 'Pluie (Wet)',
  'profiles.deleteBlocked': 'Suppression impossible — encore référencé par du matériel.',
  'profiles.emptyEngineProfiles': 'Aucun profil de moteur pour le moment.',
  'profiles.emptyTyreProfiles': 'Aucun profil de pneus pour le moment.',
  'entity.engineProfile': 'Profil de moteur',
  'entity.tyreProfile': 'Profil de pneus',

  /* ── Track Shape Studio ────────────────────────────────────── */
  'trackStudio.title': 'Studio de tracé et d’annotations de piste',
  'trackStudio.subtitle': 'Concevez la géométrie du circuit, modifiez les splines et placez les annotations de virages et de secteurs',
  'trackStudio.openStudio': 'Géométrie et annotations de piste',
  'trackStudio.modeSpline': 'Géométrie et splines',
  'trackStudio.modeCorners': 'Virages',
  'trackStudio.modeSectors': 'Secteurs',
  'trackStudio.modeSegments': 'Segments',
  'trackStudio.bakeSegments': 'Générer 200 segments',
  'trackStudio.rebakeSegments': 'Regénérer 200 segments',
  'trackStudio.segmentsBakedSuccess': '{count} segments de piste générés avec succès',
  'trackStudio.segmentsCount': '{count} segments',
  'trackStudio.segmentsStatusBaked': '200 segments générés',
  'trackStudio.segmentsStatusNotBaked': 'Non générés',
  'trackStudio.segmentsHint': 'Découpe la ligne médiane de la piste en 200 segments GPS statiques équidistants à partir de la ligne de départ / arrivée pour une comparaison de tours haute précision.',
  'trackStudio.segmentDetails': 'Détails du segment',
  'trackStudio.segmentIndex': 'Segment n°{index}',
  'trackStudio.avgSegmentLength': 'Longueur moy. de segment',
  'trackStudio.searchSegments': 'Rechercher des segments...',
  'trackStudio.modeAnnotations': 'Annotations de virages et de secteurs',
  'trackStudio.cornersCount': 'Virages de la piste ({count})',
  'trackStudio.sectorsCount': 'Secteurs de chronométrage ({count})',
  'trackStudio.clickCornersHint': "Cliquez n'importe où sur le ruban de la piste pour ajouter un virage. Faites glisser le point de corde pour le déplacer ou ajuster les points d'entrée et de sortie.",
  'trackStudio.clickSectorsHint': "Cliquez n'importe où sur le ruban de la piste pour placer un repère de secteur. Faites glisser les repères pour les repositionner le long du circuit.",
  'trackStudio.trackLength': 'Longueur de la piste : {length} m',
  'trackStudio.trackWidth': 'Largeur de la piste',
  'trackStudio.defaultTrackWidth': 'Largeur de piste par défaut',
  'trackStudio.pointWidth': 'Largeur au point',
  'trackStudio.customWidth': 'Largeur personnalisée pour ce point',
  'trackStudio.useDefaultWidth': 'Utiliser la valeur par défaut ({width} m)',
  'trackStudio.resetPointWidth': 'Réinitialiser par défaut',
  'trackStudio.segmentWidth': 'Outil de largeur de segment',
  'trackStudio.segmentWidthDesc': 'Ajustez la largeur ou créez des transitions de largeur progressives entre les points',
  'trackStudio.fromPoint': 'Du point',
  'trackStudio.toPoint': 'Au point',
  'trackStudio.startWidth': 'Largeur de départ',
  'trackStudio.endWidth': 'Largeur de fin',
  'trackStudio.transitionMode': 'Mode de transition',
  'trackStudio.modeConstant': 'Largeur constante',
  'trackStudio.modeGradient': 'Transition progressive (dégradé)',
  'trackStudio.applySegment': 'Appliquer la largeur au segment',
  'trackStudio.resetSegment': 'Réinitialiser la largeur du segment',
  'trackStudio.segmentApplied': 'Largeur mise à jour pour les points {from} à {to}',
  'trackStudio.controlPoints': 'Points de contrôle de la ligne médiane ({count})',
  'trackStudio.addPointHint': 'Cliquez sur la courbe ou le canevas pour insérer des points de contrôle. Faites glisser les points pour remodeler le tracé.',
  'trackStudio.selectedPoint': 'Point sélectionné n°{index}',
  'trackStudio.deletePoint': 'Supprimer le point',
  'trackStudio.evenSpacePoints': 'Espacer uniformément les points',
  'trackStudio.reverseDirection': 'Inverser le sens de la piste',
  'trackStudio.layerSatellite': 'Image satellite',
  'trackStudio.layerCenterline': 'Spline de la ligne médiane',
  'trackStudio.layerBoundaries': 'Limites de la piste',
  'trackStudio.layerAnnotations': 'Annotations et repères',
  'trackStudio.undo': 'Annuler (Cmd+Z)',
  'trackStudio.redo': 'Rétablir (Cmd+Maj+Z)',
  'trackStudio.reset': 'Réinitialiser les modifications',
  'trackStudio.saveSuccess': 'Tracé et annotations de la piste enregistrés avec succès !',
  'trackStudio.autoSaveSuccess': 'Tracé et annotations de la piste enregistrés automatiquement',
  'trackStudio.saveError': "Échec de l'enregistrement du tracé de la piste : {error}",
  'trackStudio.noCenterline': "Cliquez sur l'image satellite pour créer le premier point de contrôle de la ligne médiane de la piste.",
  'trackStudio.annotations': 'Annotations de la piste ({count})',
  'trackStudio.addCorner': 'Ajouter un virage (T{next})',
  'trackStudio.addSector': 'Ajouter un repère de secteur',
  'trackStudio.addFinishLine': 'Définir la ligne de départ / arrivée',
  'trackStudio.cornerName': 'Nom du virage',
  'trackStudio.cornerDirection': 'Sens du virage',
  'trackStudio.directionLeft': 'Virage à gauche',
  'trackStudio.directionRight': 'Virage à droite',
  'trackStudio.entry': 'Entrée (braquage / freinage)',
  'trackStudio.apex': 'Point de corde (Apex)',
  'trackStudio.exit': 'Sortie (débraquage / pleine charge)',
  'trackStudio.deleteAnnotation': "Supprimer l'annotation",
  'trackStudio.noAnnotations': 'Aucune annotation créée pour le moment. Cliquez sur le tracé de la piste pour placer des virages, des secteurs ou la ligne de départ/arrivée.',
  'trackStudio.finishLinePlaced': "La ligne d'arrivée est active à {pos}% du tour.",
  'trackStudio.closeConfirm': 'Vous avez des modifications non enregistrées. Voulez-vous vraiment fermer le Studio ?',
  'trackStudio.menuAddCorner': 'Ajouter un virage',
  'trackStudio.menuAddSector': 'Ajouter un repère de secteur',
  'trackStudio.menuSetFinishLine': 'Définir la ligne de départ / arrivée',
  'trackStudio.menuTrackPosition': 'Position sur la piste',
  'trackStudio.clickTrackHint': "Cliquez n'importe où sur le ruban de la piste pour ajouter un virage, un repère de secteur ou définir la ligne de départ/arrivée.",
  'trackStudio.drivingDirection': 'Sens de roulage',
  'trackStudio.reverseDrivingDirection': 'Inverser le sens de roulage',
  'trackStudio.drivingDirectionDesc': 'Inverse le sens de roulage du circuit. Toutes les séquences de virages et les secteurs de chronométrage s’adapteront automatiquement.',
  'trackStudio.multiplePointsSelected': '{count} points sélectionnés',
  'trackStudio.selectAll': 'Tout sélectionner (Cmd+A)',
  'trackStudio.deselectAll': 'Désélectionner (Échap)',
  'trackStudio.deleteSelectedPoints': 'Supprimer {count} points',
  'trackStudio.customWidthSelection': 'Largeur personnalisée (tous les points sélectionnés)',
  'tracks.reanalyzeLaps': 'Réanalyser les tours de la piste',
  'tracks.reanalyzeAllLaps': 'Réanalyser tous les tours',
  'tracks.reanalyzeQueued': '{enqueued} tour(s) mis en file d’attente pour analyse ({skipped} déjà à jour).',
  'tracks.reanalyzeForceQueued': '{enqueued} tour(s) mis en file d’attente pour un recalcul complet.',
  'tracks.reanalyzeAllUpToDate': 'Les {skipped} tour(s) sont déjà tous à jour avec les données actuelles de la piste.',
  'tracks.reanalyzeConfirmTitle': 'Réanalyser les tours de la piste',
  'tracks.reanalyzeConfirmMessage': 'L’empreinte des données de la piste « {name} » n’a pas changé ({count} tour(s) sont déjà à jour). Voulez-vous tout de même forcer un recalcul complet pour cette piste ?',
  'tracks.reanalyzeConfirmButton': 'Réanalyser les tours',
  'tracks.reanalyzeAllConfirmTitle': 'Réanalyser tous les tours',
  'tracks.reanalyzeAllConfirmMessage': 'Voulez-vous vraiment déclencher un recalcul complet de tous les tours et de toutes les sessions sur l’ensemble des pistes ?',

  /* ── Monitoring ────────────────────────────────────────────── */
  'tab.monitoring': 'Supervision',
  'monitoring.title': "Supervision des files d'attente",
  'monitoring.subtitle': "Statistiques en temps réel et contrôles de traitement des files d'analyse de tours et de sessions",
  'monitoring.refresh': 'Actualiser',
  'monitoring.autoRefreshActive': 'Actualisation auto toutes les 3 s tant que des tâches sont actives',
  'monitoring.lapQueueTitle': "File d'analyse des tours",
  'monitoring.sessionQueueTitle': "File d'analyse des sessions",
  'monitoring.pending': 'En attente',
  'monitoring.processing': 'En cours',
  'monitoring.completed': 'Terminés',
  'monitoring.failed': 'Échoués',
  'monitoring.totalEligible': 'Total éligible',
  'monitoring.actionsTitle': "Contrôles globaux de la file d'attente",
  'monitoring.syncUnanalyzed': 'Synchroniser les non analysés et obsolètes',
  'monitoring.syncUnanalyzedDesc': 'Parcourir toutes les pistes et mettre en file d’attente les tours non analysés ou dont la géométrie de piste est obsolète',
  'monitoring.syncUnanalyzedDone': '{enqueued} tour(s) mis en file d’attente ({skipped} déjà à jour).',
  'monitoring.retryFailed': 'Relancer les tâches échouées',
  'monitoring.retryFailedDesc': "Remettre en attente toutes les tâches d'analyse de tours et de sessions ayant échoué",
  'monitoring.retryFailedDone': '{retried} tâche(s) échouée(s) remise(s) en attente.',
  'monitoring.reanalyzeAll': 'Forcer la réanalyse de tous les tours',
  'monitoring.reanalyzeAllDesc': 'Forcer un recalcul complet de tous les tours et sessions sur toutes les pistes',
  'monitoring.userQueuesTitle': 'Progression par utilisateur (Round-Robin)',
  'monitoring.userQueuesSubtitle': 'Le worker traite les tâches à tour de rôle entre les utilisateurs afin que les gros lots ne bloquent jamais les autres utilisateurs',
  'monitoring.userQueuesEmpty': 'Aucune tâche active ou en attente par utilisateur.',
  'monitoring.user': 'Utilisateur',
  'monitoring.systemQueue': 'Système / Global',
  'monitoring.lapsStatus': 'Tâches de tours',
  'monitoring.sessionsStatus': 'Tâches de sessions',
  'monitoring.progress': 'Progression',
  'monitoring.tracksTitle': 'Couverture d’analyse par piste et contrôles de relance',
  'monitoring.tracksSubtitle': 'Inspectez la couverture d’analyse des tours par piste canonique et déclenchez un recalcul incrémental ou forcé',
  'monitoring.tracksEmpty': 'Aucune piste canonique configurée pour le moment.',
  'monitoring.trackCoverage': 'Couverture',
  'monitoring.upToDateLaps': 'À jour',
  'monitoring.outdatedLaps': 'Obsolètes',
  'monitoring.unanalyzedLaps': 'Non analysés',
  'monitoring.shapeReady': '200 segments prêts',
  'monitoring.noShape': 'Aucun segment généré',
  'monitoring.reanalyzeTrack': 'Synchroniser / Réanalyser',
  'monitoring.forceReanalyzeTrack': 'Tout forcer',

  /* ── Terms & Conditions ────────────────────────────────────── */
  'terms.gateTitle': "Conditions générales d'utilisation",
  'terms.gateSubtitle': "Veuillez consulter et accepter les dernières Conditions générales d'utilisation pour continuer.",
  'terms.updatedBannerTitle': "Conditions générales d'utilisation mises à jour :",
  'terms.updatedBannerDesc': "Nos Conditions générales d'utilisation ont été mises à jour. Veuillez consulter et accepter la nouvelle version pour continuer.",
  'terms.checkboxLabel': "J'ai lu et j'accepte les Conditions générales d'utilisation",
  'terms.acceptButton': 'Accepter et continuer',
  'terms.acceptingButton': 'Acceptation…',
  'terms.loadError': "Impossible de charger les Conditions générales d'utilisation. Veuillez vérifier votre connexion et réessayer.",
  'terms.acceptError': "Échec de l'enregistrement de votre acceptation. Veuillez réessayer.",

  /* ── Search & Filter Bar ───────────────────────────────────── */
  'search.placeholder': 'Rechercher ou ajouter un filtre...',
  'search.button': 'Rechercher',
  'search.syntaxHelp': 'Aide sur la syntaxe de recherche',
  'search.title': 'Guide de syntaxe de recherche',
  'search.subtitle': 'Filtrez et recherchez à l’aide de champs structurés et d’opérateurs booléens.',
  'search.closeDialog': 'Fermer',
  'search.field.bareTextDesc': 'Rechercher dans les champs texte par défaut',
  'search.operatorsTitle': 'Opérateurs',
  'search.numericComparisons': 'Comparaisons numériques / de dates',
  'search.range': 'Plage (inclusive)',
  'search.rangeDesc': 'Correspond aux valeurs comprises entre min et max',
  'search.booleanLogic': 'Logique booléenne',
  'search.booleanLogicDesc': 'Combiner plusieurs conditions',
  'search.booleanLogicUse': 'Combinez les termes avec',
  'search.booleanLogicOr': 'ou',
  'search.booleanLogicGroup': 'et groupez les expressions avec des parenthèses',
  'search.quotes': 'Chaînes entre guillemets',
  'search.quotesDesc': 'Entourez les valeurs de plusieurs mots de guillemets (ex. track:"South Garda")',
  'search.examplesTitle': 'Exemples',

  'filter.operator.eq': 'est',
  'filter.operator.neq': 'n’est pas',
  'filter.operator.gt': 'supérieur à',
  'filter.operator.gte': 'au moins',
  'filter.operator.lt': 'inférieur à',
  'filter.operator.lte': 'au plus',
  'filter.operator.range': 'entre',
  'filter.operator.contains': 'contient',
  'filter.remove': 'Supprimer le filtre',
  'filter.done': 'Terminé',
  'filter.clearInput': 'Effacer la saisie',
  'filter.selectField': 'Filtrer par champ',
  'filter.pressEnter': 'Appuyez sur Entrée pour appliquer',
  'filter.selectValues': 'Sélectionner des valeurs',
  'filter.noMatchingOptions': 'Aucune option correspondante',

  'filter.bugs.bareSearchExample': 'Rechercher un résumé, une description ou un rapporteur',
  'filter.bugs.searchSummary': 'Filtrer par résumé du bug',
  'filter.bugs.searchDescription': 'Filtrer par description du bug',
  'filter.bugs.searchReporter': 'Filtrer par nom ou e-mail du rapporteur',
  'filter.bugs.filterStatus': 'Filtrer par statut du bug',
  'filter.bugs.filterSeverity': 'Filtrer par niveau de gravité',
  'filter.bugs.filterPlatform': 'Filtrer par plateforme (web, ios, android)',
  'filter.bugs.filterDate': 'Filtrer par date de signalement',
  'filter.bugs.exampleOpenHigh': 'Bugs ouverts de gravité élevée',
  'filter.bugs.exampleMobileLogin': 'Problèmes de connexion mobile',

  'filter.users.bareSearchExample': 'Rechercher un nom, un e-mail ou un ID utilisateur',
  'filter.users.searchName': 'Filtrer par nom d’affichage',
  'filter.users.searchEmail': 'Filtrer par adresse e-mail',
  'filter.users.searchId': 'Filtrer par ID utilisateur',
  'filter.users.filterRole': 'Filtrer par rôle attribué',
  'filter.users.exampleAdmin': 'Utilisateurs ayant le rôle admin',
  'filter.users.exampleMultiRole': 'Admins ou éditeurs correspondant au nom',

  'filter.races.bareSearchExample': 'Rechercher une saison, une piste, un événement ou un pilote',
  'filter.races.searchSeason': 'Filtrer par nom de saison de championnat',
  'filter.races.filterYear': 'Filtrer par année de championnat',
  'filter.races.searchCountry': 'Filtrer par code pays',
  'filter.races.searchCategory': 'Filtrer par catégorie de course',
  'filter.races.searchTrack': 'Filtrer par nom de piste',
  'filter.races.searchEvent': 'Filtrer par titre d’événement',
  'filter.races.filterRound': 'Filtrer par numéro de manche',
  'filter.races.searchSession': 'Filtrer par nom de session',
  'filter.races.filterType': 'Filtrer par type de session',
  'filter.races.searchDriver': 'Filtrer par nom de pilote',
  'filter.races.typePractice': 'Essais libres',
  'filter.races.typeQualifying': 'Qualifications',
  'filter.races.typeHeat': 'Manche qualificative',
  'filter.races.typePrefinal': 'Pré-finale',
  'filter.races.typeFinal': 'Finale',
  'filter.races.exampleRokTrack': 'Événements ROK Cup à South Garda',
  'filter.races.exampleFinalDriver': 'Sessions finales avec un pilote donné',

  'races.season': 'Saison',
  'races.year': 'Année',
  'races.country': 'Pays',
  'races.event': 'Événement',
  'races.roundLabel': 'Manche',
  'races.session': 'Session',
  'races.type': 'Type',
  'races.label': 'Libellé',
  'races.time': 'Heure',

  'trackStudio.zoomIn': 'Zoom avant',
  'trackStudio.zoomOut': 'Zoom arrière',
  'trackStudio.resetView': 'Réinitialiser la vue',
  'trackStudio.geoLocation': 'Position géographique',
  'trackStudio.finishLineTitle': 'Ligne de départ / arrivée',
  'trackStudio.finishLineHint': 'Faites glisser le drapeau le long de la piste sur le canevas, cliquez sur la piste ou utilisez le curseur ci-dessous.',
  'trackStudio.staticGpsPosition': 'Position GPS statique',
  'trackStudio.distance': 'Distance',
  'trackStudio.previous': 'Précédent',
  'trackStudio.next': 'Suivant',

  'users.terms': 'CGU',
  'users.termsAccepted': 'Acceptées',
  'users.termsOutdated': 'Obsolètes',
  'users.termsPending': 'En attente',
  'users.resetTerms': 'Réinitialiser les conditions générales',
  'users.confirmResetTerms': 'Réinitialiser l’acceptation des conditions générales pour {name} ? Cette personne devra de nouveau consulter et accepter les conditions générales avant de continuer.',
  'users.termsReset': 'Acceptation des conditions générales réinitialisée pour {name}',
  // Filter Schema & UI Translations
  'filter.filterBy': 'Filtrer par {label}',
  'filter.removeFilter': 'Supprimer le filtre {label}',
  'filter.prevMonth': 'Mois précédent',
  'filter.nextMonth': 'Mois suivant',
  'filter.selectStartDate': 'Sélectionner la date de début dans le calendrier',
  'filter.op.exact': 'Exact',
  'filter.op.atLeast': 'Au moins',
  'filter.op.atMost': 'Au plus',
  'filter.op.between': 'Entre',
  'filter.op.greater': 'Supérieur',
  'filter.op.less': 'Inférieur',
  'filter.preset.today': 'Aujourd’hui',
  'filter.preset.last7d': '7 derniers jours',
  'filter.preset.last30d': '30 derniers jours',
  'filter.preset.last90d': '90 derniers jours',
  'filter.preset.last1y': 'Dernière année',
  'filter.preset.last7dShort': '7 dern. j',
  'filter.preset.last30dShort': '30 dern. j',
  'filter.preset.last90dShort': '90 dern. j',
  'filter.preset.last1yShort': '1 dern. an',
  'filter.sessionType.race': 'Course',
  'filter.sessionType.final': 'Finale',
  'filter.sessionType.pre_final': 'Pré-finale',
  'filter.sessionType.heat': 'Manche',
  'filter.sessionType.qualifying': 'Qualifications',
  'filter.unit.laps': 'tours',
  'filter.field.date': 'Date',
  'filter.field.track': 'Piste',
  'filter.desc.track': 'Correspond au nom de la piste',
  'filter.field.championship': 'Championnat',
  'filter.desc.championship': 'Nom du championnat',
  'filter.field.category': 'Catégorie',
  'filter.desc.category': 'Catégorie de course canonique',
  'filter.field.sessionType': 'Type de session',
  'filter.desc.sessionType': 'Classification de session (course, manche, qualifications, finale)',
  'filter.field.laps': 'Tours',
  'filter.field.season': 'Saison',
  'filter.desc.season': 'Correspond à l’année de la saison',
  'filter.field.driver': 'Pilote',
  'filter.placeholder.driver': 'ex. Yanick',
  'filter.placeholder.email': 'ex. gmail.com',
  'filter.desc.email': 'Correspond à l’adresse e-mail',
  'bugs.severity': 'Gravité',
  'bugs.severityCritical': 'Critique',
  'bugs.severityHigh': 'Élevée',
  'bugs.severityMedium': 'Moyenne',
  'bugs.severityLow': 'Faible',
  'bugs.statusInProgress': 'En cours',
  'bugs.statusClosed': 'Fermé',
  'bugs.submitter': 'E-mail du déclarant',
  'filter.bugs.title': 'Syntaxe de recherche des rapports de bug',
  'filter.bugs.subtitle': 'Filtrer les bugs signalés par statut, gravité, e-mail ou mots-clés.',
  'filter.field.bugTitle': 'Résumé / Titre',
  'filter.placeholder.bugTitle': 'ex. crash, import',
  'filter.desc.bugTitle': 'Correspond au titre ou à la description du problème',
  'filter.desc.bugStatus': 'Statut du problème (ouvert, en cours, fermé)',
  'filter.desc.bugSeverity': 'Niveau de gravité du bug',
  'filter.placeholder.bugEmail': 'ex. user@example.com',
  'filter.desc.bugEmail': 'Correspond à l’e-mail du déclarant',
  'filter.field.bugEnv': 'Environnement',
  'filter.desc.bugEnv': 'Filtrer par environnement (prod, dev, local)',
  'filter.bugs.example1': 'Tous les rapports de bug ouverts',
  'filter.bugs.example2': 'Problèmes ouverts en production',
  'filter.bugs.example3': 'Rapports de crash ouverts',
  'filter.field.driverEntries': 'Pilotes engagés',
  'filter.desc.driverEntries': 'Nombre total de pilotes participant à la course',
  'filter.preset.entriesSmall': 'Petit (< 10)',
  'filter.preset.entriesMedium': 'Moyen (10..25)',
  'filter.preset.entriesLarge': 'Grand (> 25)',
  'filter.desc.raceLaps': 'Nombre de tours de course complétés',
  'filter.desc.raceDriver': 'Correspond au nom du pilote dans les résultats de course',
  'filter.desc.raceDate': 'Filtrer les courses par période ou plage de dates du calendrier',
  'filter.races.example1': 'Courses 2026 à Wohlen',
  'filter.races.example2': 'Grilles importantes en Senior MAX',
  'filter.races.example3': 'Courses avec Yanick au cours de la dernière année',
  'filter.users.title': 'Syntaxe de recherche des utilisateurs',
  'filter.users.subtitle': 'Filtrer les utilisateurs inscrits par nom, e-mail, rôle ou fournisseur d’authentification.',
  'users.colName': 'Nom',
  'users.colEmail': 'E-mail',
  'users.colRole': 'Rôle',
  'users.colProvider': 'Fournisseur d’auth',
  'users.roleAdmin': 'Admin',
  'users.roleOwner': 'Propriétaire',
  'users.roleUser': 'Utilisateur',
  'filter.desc.userName': 'Correspond au nom d’affichage de l’utilisateur',
  'filter.desc.userRole': 'Rôle de permission (admin, propriétaire, utilisateur)',
  'filter.desc.authProvider': 'Fournisseur d’authentification',
  'filter.provider.password': 'Mot de passe',
  'filter.desc.userDate': 'Date de création ou de dernière activité',
  'filter.users.example1': 'Trouver les utilisateurs correspondant à Yanick',
  'filter.users.example2': 'Administrateurs de la plateforme',
  'filter.users.example3': 'Connexions Google des 30 derniers jours',
};

export default fr;

